import './styles/gameOverDialog.css';
import Dialog from '../../subcomponents/Dialog';
import { useGameStore } from '../../../store/gameStore';
import { useShallow } from 'zustand/shallow';
import Btn from '../../subcomponents/Btn';
import { useEffect, useState } from 'react';
import { Star, Frown, Crown } from 'lucide-react';
import { DateTime } from 'luxon';
import { isMobile } from 'react-device-detect';
import { getResultText, getStarCount, getStarDisplay } from '../../utils/scoreResultHelper';
import { toast } from 'react-hot-toast';
import CustomToast from '../../subcomponents/Toast';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { getDayNumber } from '../../utils/dayCounterHelper';

type GameOverDialogProps = {
  open: boolean;
  onClose: () => void;
};

function GameOverDialog(props: GameOverDialogProps) {
  const { open, onClose } = props;

  // Subscribe to game store
  const { mode, foundWords, points } = useGameStore(
    useShallow((state) => ({
      mode: state.mode,
      foundWords: state.gameData[state.mode].foundWords,
      points: state.gameData[state.mode].points,
    }))
  );

  const getTitle = () => {
    switch (mode) {
      case 'blitzSimple':
        return '4x4 BLITZ';
      case 'blitzDeluxe':
        return '5x5 BLITZ';
      case 'endlessSimple':
        return '4x4 ENDLESS';
      case 'endlessDeluxe':
        return '5x5 ENDLESS';
    }
  };

  const [timeRemaining, setTimeRemaining] = useState<string>('');

  useEffect(() => {
    const updateTimeRemaining = () => {
      const now = DateTime.now().setZone('America/New_York');

      const next3am =
        now.hour < 3
          ? now.set({ hour: 3, minute: 0, second: 0 })
          : now.plus({ days: 1 }).set({ hour: 3, minute: 0, second: 0 });

      const diff = next3am.diff(now, ['hours', 'minutes', 'seconds']).toObject();

      const hours = Math.floor(diff.hours ?? 0)
        .toString()
        .padStart(2, '0');
      const minutes = Math.floor(diff.minutes ?? 0)
        .toString()
        .padStart(2, '0');
      const seconds = Math.floor(diff.seconds ?? 0)
        .toString()
        .padStart(2, '0');

      if (hours === '00' && minutes === '00' && seconds === '00') {
        setTimeout(() => window.location.reload(), 900);
      }

      setTimeRemaining(`${hours}:${minutes}:${seconds}`);
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 1000);
    return () => clearInterval(interval);
  }, []);

  const showCopiedToClipboardToast = () => {
    toast.custom((t) => <CustomToast t={t} message={`Copied results to clipboard`} success />, {
      duration: 2000,
    });
  };

  const APP_STORE_URL = 'https://apps.apple.com/app/id6746722980';

  const handleShare = async () => {
    const isNative = Capacitor.isNativePlatform();
    const shareUrl = isNative ? APP_STORE_URL : window.location.href;

    const data = {
      title: `Daily LetterSwap #${getDayNumber()}`,
      text: `Daily LetterSwap #${getDayNumber()}\n${getTitle()}: ${getStarDisplay()}\n${foundWords.length} ${
        foundWords.length === 1 ? 'word' : 'words'
      } for ${points} points.`,
      url: shareUrl,
    };

    const textToCopy = `${data.text}\n${data.url}`;

    if (isNative) {
      try {
        await Share.share({
          title: data.title,
          text: data.text,
          url: data.url,
          dialogTitle: 'Share your result',
        });
      } catch (err: any) {
        console.error('Capacitor share failed, fallback to clipboard:', err);
        if (err?.message === 'Share canceled') return;
        try {
          await navigator.clipboard.writeText(textToCopy);
          showCopiedToClipboardToast();
        } catch (err) {
          console.error('Clipboard fallback failed:', err);
        }
      }
    } else if (isMobile && typeof navigator.share === 'function') {
      try {
        await navigator.share(data);
      } catch (err: any) {
        if (err?.message === 'Share canceled') return;
        console.error('Web Share API failed, fallback to clipboard:', err);
        try {
          await navigator.clipboard.writeText(textToCopy);
          showCopiedToClipboardToast();
        } catch (err) {
          console.error('Clipboard fallback failed:', err);
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(textToCopy);
        showCopiedToClipboardToast();
      } catch (err: any) {
        console.error('Clipboard copy failed:', err);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title={getTitle()}>
      <div className="game-over-container">
        <div className="results-text">
          Today's result:
          <div className="flex items-center mb-[1px]">
            {getStarCount() === 0 && (
              <span className="flex gap-2">
                No stars <Frown className="frown-icon" />
              </span>
            )}
            {getStarCount() === 6 && (
              <span className="flex gap-2">
                Grandmaster <Crown className="crown-icon" />
              </span>
            )}
            {getStarCount() < 6 &&
              [...Array(getStarCount())].map((_, idx) => <Star key={idx} className="star-icon" />)}
          </div>
        </div>
        <div className="results-description">
          {getResultText()} You created <b>{foundWords.length}</b>{' '}
          {foundWords.length === 1 ? 'word' : 'words'} for <b>{points}</b> points.
        </div>
        <div className="share-game-row">
          <div className="next-game-col">
            <span className="text-[var(--text-secondary)]">Next Puzzle</span>
            <span>{timeRemaining}</span>
          </div>
          <Btn text="SHARE" appendIcon="Share" onClick={() => handleShare()} />
        </div>
        <div className="daily-game-text">Daily LetterSwap #{getDayNumber()}</div>
      </div>
    </Dialog>
  );
}

export default GameOverDialog;
